import { useEffect, useMemo, useRef, useState } from "react"
import { createChildBridge } from "../child/child-bridge"

/**
 * ChildPlayground
 *
 * This component runs INSIDE the iframe.
 * It initializes the child bridge, performs handshake,
 * and exposes buttons to trigger navigation events.
 */
export function ChildPlayground() {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // In playground mode we use same-origin parent
  const parentOrigin = useMemo(() => window.location.origin, [])

  // Keep bridge instance in a ref so it can be used by UI handlers
  const bridgeRef = useRef<ReturnType<typeof createChildBridge> | null>(null)

  useEffect(() => {
    /**
     * Create child bridge.
     * In real integration, parentOrigin would be a fixed external domain.
     */
    const bridge = createChildBridge({
      parentTargetOrigin: parentOrigin,
      allowedParentOrigins: [parentOrigin],

      // Called when handshake completes
      onReady: () => setReady(true),

      // Called when parent sends an ERROR message
      onError: (error) => setError(error?.message),

      // Called when a valid Parent → Child command/event is received
      onEvent: (msg, meta) => {
        // Handle incoming commands from the parent here
        console.log("msg", msg, "meta: ", meta)
      },
    })

    bridgeRef.current = bridge

    // Attach message listener first
    bridge.attach()

    // Start handshake (SYNC → SYNC_ACK → ACK)
    bridge.sendSync()

    // Cleanup on unmount
    return () => {
      bridge.detach()
      bridgeRef.current = null
    }
  }, [parentOrigin])

  return (
    <div style={{ padding: 16, fontFamily: "system-ui" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <b>Child Playground</b>

        {/* Handshake status indicator */}
        <span
          style={{
            padding: "4px 8px",
            borderRadius: 999,
            border: "1px solid #333",
            background: ready ? "#0b3" : "#999",
            color: "#000",
          }}
        >
          {ready ? "READY" : "NOT READY"}
        </span>
      </div>

      {/* Error display */}
      {error && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            border: "1px solid #f33",
            borderRadius: 8,
          }}
        >
          <b>Error:</b> {error}
        </div>
      )}

      {/* Navigation action buttons */}
      <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
        <button
          disabled={!ready}
          onClick={() => bridgeRef.current?.nav.openSearch("coffee")}
        >
          nav.openSearch("coffee")
        </button>

        <button
          disabled={!ready}
          onClick={() => bridgeRef.current?.nav.openPlace("place-123")}
        >
          nav.openPlace("place-123")
        </button>

        <button
          disabled={!ready}
          onClick={() => bridgeRef.current?.nav.openDirectory()}
        >
          nav.openDirectory()
        </button>

        <button
          disabled={!ready}
          onClick={() =>
            bridgeRef.current?.nav.showDirections("place-111", "place-222")
          }
        >
          nav.showDirections("place-111", "place-222")
        </button>

        <button
          disabled={!ready}
          onClick={() =>
            bridgeRef.current?.nav.nextStepNavigation(
              "place-111",
              "place-222",
              2
            )
          }
        >
          nav.nextStepNavigation("place-111", "place-222", 2)
        </button>

        <button
          disabled={!ready}
          onClick={() =>
            bridgeRef.current?.nav.nextStepNavigation(
              "place-111",
              "place-222",
              1
            )
          }
        >
          nav.prevStepNavigation("place-111", "place-222", 1)
        </button>

        <button
          disabled={!ready}
          onClick={() =>
            bridgeRef.current?.nav.startNavigation("place-111", "place-222")
          }
        >
          nav.startNavigation("place-111", "place-222")
        </button>
      </div>

      <div style={{ marginTop: 12, fontSize: 12, opacity: 0.7 }}>
        Tip: If buttons are disabled, the handshake has not completed yet.
      </div>
    </div>
  )
}
